import { ReactNode, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRTLDirection } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

interface ScrollRevealProps {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  className?: string;
}

const ScrollReveal = ({
  children,
  direction = 'up',
  delay = 0,
  className = '',
}: ScrollRevealProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<gsap.core.Tween | null>(null);
  const hasAnimatedRef = useRef(false);
  const { getDirection } = useRTLDirection();

  useEffect(() => {
    const element = elementRef.current;
    if (!element || hasAnimatedRef.current) return;

    // Get RTL-aware direction
    let finalDirection = direction;
    if (direction === 'left' || direction === 'right') {
      finalDirection = getDirection(direction);
    }

    const directions = {
      up: { y: 50, x: 0 },
      down: { y: -50, x: 0 },
      left: { y: 0, x: 50 },
      right: { y: 0, x: -50 },
    };

    const from = directions[finalDirection];

    // Check if element is already visible (to prevent animation if scrolled past)
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight * 0.8;

    const animation = gsap.fromTo(
      element,
      {
        opacity: isVisible ? 1 : 0,
        ...(isVisible ? { x: 0, y: 0 } : from),
      },
      {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 1,
        delay: isVisible ? 0 : delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          once: true, // Only animate once
          onEnter: () => {
            hasAnimatedRef.current = true;
          },
        },
        immediateRender: isVisible, // Skip animation if already visible
      }
    );

    animationRef.current = animation;

    // If already visible, mark as animated
    if (isVisible) {
      hasAnimatedRef.current = true;
    }

    // Cleanup function
    return () => {
      if (animationRef.current?.scrollTrigger) {
        animationRef.current.scrollTrigger.kill();
      }
      if (animationRef.current) {
        animationRef.current.kill();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  return (
    <div ref={elementRef} className={className}>
      {children}
    </div>
  );
};

export default ScrollReveal;

